import { AspAgingBucketKey, AspAgingSummary, Tooltip, formattedCurrency, withStyles } from 'vopay';
import { Fragment } from 'react';

import { useTranslation } from 'react-i18next';
import styles from './AgingOverview.module.scss';

import { Skeleton } from '#shared/components/UI';

const cn = withStyles(styles);

const BUCKETS: AspAgingBucketKey[] = ['Current', 'PastDue1To30', 'PastDue31To60', 'PastDue61To90', 'PastDue91Plus'];

const ZERO_BUCKET = { Amount: 0, Count: 0 };

// Rendered when there are no open items at all — a single all-zero row.
const ZERO_SUMMARY: AspAgingSummary = {
  TotalAmount: 0,
  TotalCount: 0,
  Currency: null,
  AgingBuckets: {
    Current: ZERO_BUCKET,
    PastDue1To30: ZERO_BUCKET,
    PastDue31To60: ZERO_BUCKET,
    PastDue61To90: ZERO_BUCKET,
    PastDue91Plus: ZERO_BUCKET,
  },
};

type AgingOverviewProps = {
  // One summary per currency with open items — every currency renders its own
  // header row (the list's currency filter narrows the list, not the overview,
  // per the multi-currency Figma section).
  summaries: AspAgingSummary[];
  type: 'Payable' | 'Receivable';
  // Currency the zero state renders in when there are no open items.
  emptyStateCurrency: string;
  isFetching: boolean;
  onBucketClick: (bucket: AspAgingBucketKey, currency: string) => void;
};

export default function AgingOverview({
  summaries,
  type,
  emptyStateCurrency,
  isFetching,
  onBucketClick,
}: AgingOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.AgingOverview' });

  if (isFetching && !summaries.length) {
    // Mirrors the loaded layout (same grid, gap and card radius) so nothing
    // shifts when the summary lands.
    return (
      <div className={cn('overview')} data-testid="aging-overview">
        <Skeleton height={92} borderRadius={16} />
        <Skeleton height={92} borderRadius={16} />
      </div>
    );
  }

  const bucketLabels: { [bucket in AspAgingBucketKey]: string } = {
    Current: t('current', { defaultValue: 'Current' }),
    PastDue1To30: t('pastDue1To30', { defaultValue: 'Past Due 1 - 30 Days' }),
    PastDue31To60: t('pastDue31To60', { defaultValue: 'Past Due 31 - 60 Days' }),
    PastDue61To90: t('pastDue61To90', { defaultValue: 'Past Due 61 - 90 Days' }),
    PastDue91Plus: t('pastDue91Plus', { defaultValue: 'Past Due 91+ Days' }),
  };

  const items =
    type === 'Receivable'
      ? t('itemsReceivable', { defaultValue: 'invoices' })
      : t('itemsPayable', { defaultValue: 'bills' });

  const totalOwedLabel =
    type === 'Receivable'
      ? t('totalOwedReceivable', { defaultValue: 'Total Owed by Customers (All-Time)' })
      : t('totalOwedPayable', { defaultValue: 'Total Owed to Suppliers (All-Time)' });

  const rows = summaries.length ? summaries : [ZERO_SUMMARY];
  // A single currency looks exactly like the aging-tiles design; the row
  // label only appears once there is a second header to tell apart (Figma
  // "Payables — Open Payable — Multi Currencies").
  const hasMultipleCurrencies = rows.length > 1;

  return (
    <div data-testid="aging-overview">
      {rows.map((summary) => {
        const currency = summary.Currency || emptyStateCurrency;
        const amountOf = (amount: number) => `${currency} ${formattedCurrency(amount.toFixed(2))}`;

        const bucketTooltips: { [bucket in AspAgingBucketKey]: string } = {
          Current: t('currentTooltip', { defaultValue: 'See {{currency}} {{items}} not yet due', currency, items }),
          PastDue1To30: t('pastDueTooltip', {
            defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
            currency,
            items,
            range: '1 - 30',
          }),
          PastDue31To60: t('pastDueTooltip', {
            defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
            currency,
            items,
            range: '31 - 60',
          }),
          PastDue61To90: t('pastDueTooltip', {
            defaultValue: 'See {{currency}} {{items}} past due {{range}} days',
            currency,
            items,
            range: '61 - 90',
          }),
          PastDue91Plus: t('pastDue91PlusTooltip', {
            defaultValue: 'See {{currency}} {{items}} past due 91+ days',
            currency,
            items,
          }),
        };

        return (
          <div className={cn('overview', hasMultipleCurrencies && 'labelled')} key={currency}>
            {hasMultipleCurrencies && (
              <div className={cn('currencyLabel')} data-testid={`aging-currency-label-${currency}`}>
                {currency}
              </div>
            )}

            <div className={cn('totalCard')}>
              <div className={cn('label')}>{totalOwedLabel}</div>
              <div className={cn('amount')}>{amountOf(summary.TotalAmount)}</div>
            </div>

            <div className={cn('tilesCard')}>
              {BUCKETS.map((bucket, index) => (
                <Fragment key={bucket}>
                  {index > 0 && <div className={cn('divider')} />}

                  <div className={cn('tileWrap')}>
                    <Tooltip
                      type="hover"
                      position="top"
                      hasButton={false}
                      information={bucketTooltips[bucket]}
                      customElement={
                        <button
                          type="button"
                          className={cn('tile')}
                          data-testid={`aging-tile-${currency}-${bucket}`}
                          aria-label={`${bucketLabels[bucket]} — ${bucketTooltips[bucket]}`}
                          onClick={() => onBucketClick(bucket, currency)}
                        >
                          <div className={cn('label')}>{bucketLabels[bucket]}</div>
                          <div className={cn('amount')}>{amountOf(summary.AgingBuckets[bucket].Amount)}</div>
                        </button>
                      }
                    />
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
